import React, { useRef, useState } from "react";
import { PageHeader, Card, Button, Spinner, Badge } from "@/components/ui";
import { endpoints, apiError, pollJob, api } from "@/lib/api";
import EntityProfile, { SourceLabel } from "@/components/EntityProfile";
import { UploadCloud, FileSpreadsheet, Download, ChevronDown, ChevronRight } from "lucide-react";

function Dropzone({ onFile, disabled }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      onClick={() => !disabled && ref.current?.click()}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-10 md:p-14 text-center transition-colors ${over ? "border-[#C5A059] bg-[#C5A059]/[0.06]" : "border-white/15 hover:border-[#C5A059]/60"}`}
      data-testid="batch-dropzone"
    >
      <UploadCloud size={30} className="mx-auto text-gold mb-3" />
      <div className="text-white/85 text-sm">Drag & drop a CSV or Excel file, or click to browse</div>
      <div className="text-white/40 text-[12px] mt-1">Required column: company name · optional: country, VAT ID, registration number · max 200 rows</div>
      <input ref={ref} type="file" accept=".csv,.tsv,.xlsx,.xls" className="hidden" data-testid="batch-file-input"
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </div>
  );
}

function Row({ r }) {
  const [open, setOpen] = useState(false);
  const [entity, setEntity] = useState(null);
  const toggle = async () => {
    setOpen(!open);
    if (!entity && r.entity_id) setEntity((await endpoints.entity(r.entity_id).catch(() => ({ data: null }))).data);
  };
  const s = r.summary || {};
  return (
    <>
      <tr onClick={toggle} className="border-b border-white/5 text-[12.5px] cursor-pointer hover:bg-white/[0.02]" data-testid={`batch-row-${r.row}`}>
        <td className="py-2.5 pl-2">{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
        <td>{r.input?.name}</td>
        <td className="text-white">{s.name || "—"}</td>
        <td>{s.country || r.input?.country || "—"}</td>
        <td className="font-mono">{s.vat_id || "—"}</td>
        <td className="max-w-[220px] truncate">{s.primary_sector || "—"}</td>
        <td>{r.status === "matched" ? <SourceLabel source={r.source} /> : <Badge tone="red">not found</Badge>}</td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="p-4 bg-white/[0.015]" data-testid={`batch-row-profile-${r.row}`}>
          {r.entity_id ? (entity ? <EntityProfile entity={entity} testid={`batch-entity-${r.row}`} /> : <Spinner />) : <div className="text-white/45 text-sm">No match. {r.error}</div>}
        </td></tr>
      )}
    </>
  );
}

async function download(url, filename) {
  const r = await api.get(url.replace(/^.*\/api/, ""), { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function BatchImport() {
  const [job, setJob] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(0);

  const onFile = async (file) => {
    setErr(""); setJob(null); setMeta(null); setUploading(1);
    try {
      const r = await endpoints.batchImport(file, (p) => setUploading(Math.max(1, Math.round((p.loaded / (p.total || 1)) * 100))));
      setMeta({ ...r.data, filename: file.name });
      setUploading(0);
      await pollJob(`/jobs/${r.data.job_id}`, setJob, 3000);
    } catch (ex) { setErr(apiError(ex)); setUploading(0); }
  };

  const rows = (job?.result?.rows || job?.partial || []).filter(Boolean).sort((a, b) => a.row - b.row);
  const running = job && job.status !== "ready" && job.status !== "error";

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader eyebrow="Batch Import" title={<>Enrich a <span className="italic text-gold">company list</span>.</>}
        description="Each row is resolved through Global Database → Cerved (Italy) → Tavily (international) and stored in the Company Entity layer." testid="batch-import-page" />
      <Card hover={false}><Dropzone onFile={onFile} disabled={!!uploading || running} /></Card>
      {uploading > 0 && <div className="text-[12px] text-white/60 flex items-center gap-2" data-testid="batch-uploading"><Spinner size={12} /> Uploading… {uploading}%</div>}
      {err && <div className="text-red-300 text-sm" data-testid="batch-error">{err}</div>}
      {meta && (
        <Card hover={false} data-testid="batch-results-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet size={18} className="text-gold" />
              <div>
                <div className="text-sm text-white">{meta.filename}</div>
                <div className="text-[11px] text-white/45" data-testid="batch-progress">
                  {job ? `${job.progress || 0} / ${meta.total} rows processed · ${job.status}` : "Queued…"}
                  {job?.result && ` · ${job.result.matched} matched`}
                </div>
              </div>
              {running && <Spinner size={14} />}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!rows.length} onClick={() => download(endpoints.batchExportUrl(meta.job_id, "csv"), "clara_enriched.csv")} data-testid="batch-export-csv"><Download size={12} /> CSV</Button>
              <Button size="sm" disabled={!rows.length} onClick={() => download(endpoints.batchExportUrl(meta.job_id, "xlsx"), "clara_enriched.xlsx")} data-testid="batch-export-xlsx"><Download size={12} /> Excel</Button>
            </div>
          </div>
          <div className="h-1 rounded bg-white/5 mb-4 overflow-hidden"><div className="h-full bg-[#C5A059] transition-all" style={{ width: `${((job?.progress || 0) / (meta.total || 1)) * 100}%` }} /></div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="batch-results-table">
              <thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-white/40 border-b border-white/10">
                <th className="w-6" /><th className="py-2">Input</th><th>Resolved name</th><th>Country</th><th>VAT</th><th>Sector</th><th>Source</th>
              </tr></thead>
              <tbody>{rows.map((r) => <Row key={r.row} r={r} />)}</tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
